import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Plus, BookOpen, Eye, Edit3, MoreHorizontal, Clock, Users, Star } from '../../components/CustomIcons';

export default function ProviderCourses() {
  const { authFetch } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    authFetch('/marketplace/provider/courses')
      .then(r => setCourses(r.data || []))
      .catch(e => console.error('Load courses error:', e))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStatusChange = async (courseId, newStatus) => {
    try {
      await authFetch(`/marketplace/provider/courses/${courseId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      setCourses(prev => prev.map(c => c.id === courseId ? { ...c, status: newStatus } : c));
    } catch (e) { console.error('Course status change error:', e); }
  };

  const filtered = statusFilter === 'all' ? courses : courses.filter(c => c.status === statusFilter);

  return (
    <div className="page-wrapper" style={{ padding: '24px', background: 'var(--bg-page)', minHeight: '100vh' }} data-testid="provider-courses">
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <div className="overline" style={{ marginBottom: '4px' }}>Provider Portal</div>
            <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '26px', color: 'var(--text-primary)', margin: 0 }}>My Courses</h1>
          </div>
          <Link to="/marketplace/provider/courses/new" className="btn-primary" style={{ textDecoration: 'none' }} data-testid="add-course-btn">
            <Plus size={16} /> New Course
          </Link>
        </div>

        {/* Filter Tabs */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', background: 'var(--bg-elevated)', padding: '4px', borderRadius: '10px', width: 'fit-content' }}>
          {['all', 'published', 'draft'].map(f => (
            <button key={f} onClick={() => setStatusFilter(f)}
              style={{ padding: '7px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, fontFamily: 'Plus Jakarta Sans, sans-serif', background: statusFilter === f ? 'var(--bg-surface)' : 'transparent', color: statusFilter === f ? 'var(--text-primary)' : 'var(--text-muted)', transition: 'all 0.18s ease', boxShadow: statusFilter === f ? 'var(--shadow)' : 'none' }}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Courses List */}
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[...Array(4)].map((_, i) => <div key={i} className="skeleton" style={{ height: '88px', borderRadius: '14px' }} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="card" style={{ padding: '60px 24px', textAlign: 'center' }}>
            <BookOpen size={40} style={{ color: 'var(--text-muted)', marginBottom: '12px' }} />
            <p style={{ color: 'var(--text-muted)', fontSize: '15px', marginBottom: '16px' }}>
              {statusFilter === 'all' ? 'No courses yet.' : `No ${statusFilter} courses.`}
            </p>
            {statusFilter === 'all' && (
              <Link to="/marketplace/provider/courses/new" className="btn-primary" style={{ textDecoration: 'none' }}>
                Create Your First Course
              </Link>
            )}
          </div>
        ) : (
          <div className="card" style={{ overflow: 'hidden' }}>
            {filtered.map((course, i) => (
              <div key={course.id} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px 20px', borderBottom: i < filtered.length - 1 ? '1px solid var(--border-subtle)' : 'none', transition: 'background 0.15s ease' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

                <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: 'var(--orange-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <BookOpen size={20} style={{ color: 'var(--orange)' }} />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)', margin: '0 0 4px 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {course.title}
                  </p>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Clock size={11} /> {course.duration_hours}h
                    </span>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Users size={11} /> {course.enrolled_count || 0}
                    </span>
                    {course.rating > 0 && (
                      <span style={{ fontSize: '12px', color: 'var(--orange)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Star size={11} fill="var(--orange)" /> {course.rating}
                      </span>
                    )}
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      {course.price_credits === 0 ? 'Free' : `${course.price_credits.toLocaleString()} credits`}
                    </span>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '5px', background: course.status === 'published' ? 'var(--success-bg)' : 'var(--warning-bg)', color: course.status === 'published' ? 'var(--success)' : 'var(--warning)' }}>
                    {course.status}
                  </span>

                  <select value={course.status} onChange={e => handleStatusChange(course.id, e.target.value)}
                    style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: '7px', color: 'var(--text-secondary)', fontSize: '12px', padding: '4px 8px', cursor: 'pointer' }}
                    data-testid={`course-status-${course.id}`}>
                    <option value="draft">Draft</option>
                    <option value="published">Publish</option>
                    <option value="archived">Archive</option>
                  </select>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
